import {
  Avatar,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  hasProfilePicture,
} from "../../utils/avatarUtils";

const formatDate = (iso, isRTL) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(isRTL ? "ar-SA" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const textCellSx = { textAlign: "start" };

const ActiveUsersTable = ({ users, isRTL }) => {
  const { t } = useTranslation();

  return (
    <Card
      dir={isRTL ? "rtl" : "ltr"}
      sx={{ borderRadius: 2, boxShadow: 1, textAlign: "start" }}
    >
      <CardContent sx={{ textAlign: "start" }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ mb: 2, fontWeight: 600, textAlign: "start" }}
        >
          {t("reports.activeUsers.title")}
        </Typography>
        <TableContainer dir={isRTL ? "rtl" : "ltr"}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={textCellSx}>{t("reports.activeUsers.username")}</TableCell>
                <TableCell align="center">{t("reports.activeUsers.avatar")}</TableCell>
                <TableCell align="center">{t("reports.activeUsers.replyCount")}</TableCell>
                <TableCell align="center">
                  {t("reports.activeUsers.ticketsCreated")}
                </TableCell>
                <TableCell align="center">
                  {t("reports.activeUsers.ticketsClosed")}
                </TableCell>
                <TableCell sx={textCellSx}>{t("reports.activeUsers.lastActivity")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!users?.length ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {t("reports.noData")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const avatarUser = {
                    id: user.user_id,
                    username: user.username,
                    arabic_name: user.arabic_name,
                    profile_picture: user.profile_picture,
                  };
                  return (
                    <TableRow key={user.user_id} hover>
                      <TableCell sx={textCellSx}>
                        <Typography variant="body2" fontWeight={500} sx={{ textAlign: "start" }}>
                          {isRTL ? user.arabic_name : user.username}
                        </Typography>
                        {isRTL && user.username !== user.arabic_name && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textAlign: "start", display: "block" }}
                          >
                            {user.username}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Avatar
                          src={getAvatarImageSrc(avatarUser)}
                          sx={{
                            width: 36,
                            height: 36,
                            mx: "auto",
                            bgcolor: hasProfilePicture(avatarUser)
                              ? undefined
                              : getAvatarColor(avatarUser),
                          }}
                        >
                          {!hasProfilePicture(avatarUser) &&
                            getAvatarInitials(avatarUser)}
                        </Avatar>
                      </TableCell>
                      <TableCell align="center">{user.reply_count}</TableCell>
                      <TableCell align="center">{user.tickets_created}</TableCell>
                      <TableCell align="center">{user.tickets_closed}</TableCell>
                      <TableCell sx={textCellSx}>
                        {formatDate(user.last_reply_date, isRTL)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default ActiveUsersTable;
